import React, { useState, useMemo } from 'react';
import { dateTypes, getDateTypeLabel, DEPARTURE_DATE_TYPES } from '../../utils/dateTypeUtils';
import { TextField, DatePicker, Autocomplete } from '@anyemp/ui-kit';
import {
  CalendarDays,
  Plus,
  Trash2,
  Calendar,
  Clock,
  AlertCircle,
  X
} from 'lucide-react';
import { fmtTzDateTimeHuman } from '../../utils/dateFormatUtils';

const ImportantDatesManager = ({
  entityId,
  entityType = 'JobApplication', // 'Talent' или 'JobApplication'
  importantDates = [],
  onImportantDatesChange,
  apiMethods = null, // Объект с методами API для работы с датами
  showTitle = true,
  title = "Important Dates",
  className = "",
  mode = 'list', // 'list' | 'single' — режим единой формы редактирования
  allowAddRemove = true // управляет кнопками добавления/удаления в list-режиме
}) => {
  const [error, setError] = useState(null);

  // Сортируем типы дат в алфавитном порядке по label
  const sortedDateTypes = useMemo(() => {
    return [...dateTypes].sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const addImportantDate = () => {
    const newDate = {
      id: Date.now(), // Временный ID
      date_type: '',
      date_value: '',
      description: '',
      is_overdue: false,
      is_upcoming: false,
      created_at: new Date().toISOString()
    };

    onImportantDatesChange([...importantDates, newDate]);
  };

  const removeImportantDate = (index) => {
    onImportantDatesChange(importantDates.filter((_, i) => i !== index));
  };

  const updateImportantDate = (index, field, value) => {
    const updatedDates = importantDates.map((date, i) => {
      if (i === index) {
        const updated = { ...date, [field]: value };

        // Обновляем статусы при изменении date_value
        if (field === 'date_value' && value) {
          const dateValue = new Date(value);
          const now = new Date();
          updated.is_overdue = dateValue < now;
          updated.is_upcoming = dateValue > now && dateValue <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        }

        return updated;
      }
      return date;
    });
    onImportantDatesChange(updatedDates);
  };

  const formatDateTime = (dateString) => fmtTzDateTimeHuman(dateString);

  const getDateStatusBadge = (date) => {
    if (date.is_overdue) {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs rounded">
          <AlertCircle className="w-3 h-3 mr-1" />
          Overdue
        </span>
      );
    } else if (date.is_upcoming) {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs rounded">
          <Clock className="w-3 h-3 mr-1" />
          Upcoming
        </span>
      );
    }
    return null;
  };

  const isValidDate = (date) => {
    return date.date_type && date.date_value;
  };

  return (
    <div className={`bg-light-bg-primary dark:bg-dark-bg-secondary shadow rounded-lg p-6 border border-light-border-primary dark:border-dark-border-primary ${className}`}>
      {showTitle && mode === 'list' && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary flex items-center">
            <CalendarDays className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
            {title}
          </h3>
          {allowAddRemove && (
            <button
              type="button"
              onClick={addImportantDate}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Important Date
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-300 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-3 inline-flex items-center justify-center h-5 w-5 text-red-400 dark:text-red-300 hover:text-red-600 dark:hover:text-red-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {importantDates.length === 0 && mode === 'list' ? (
        <div className="text-center py-8">
          <Calendar className="h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary mx-auto mb-4" />
          <p className="mt-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">No important dates added</p>
          <p className="mt-1 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">Click "Add Important Date" to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(mode === 'single' ? importantDates.slice(0, 1) : importantDates).map((date, index) => (
            <div
              key={date.id || index}
              className={`border rounded-lg p-4 transition-colors ${
                isValidDate(date)
                  ? 'border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-tertiary'
                  : 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  {date.date_type && (
                    <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                      {getDateTypeLabel(date.date_type)}
                    </span>
                  )}
                  {getDateStatusBadge(date)}
                </div>
                {mode === 'list' && allowAddRemove && (
                  <button
                    type="button"
                    onClick={() => removeImportantDate(index)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                    title="Remove date"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Autocomplete
                    fullWidth
                    label="Date Type *"
                    value={date.date_type}
                    onChange={(value) => updateImportantDate(index, 'date_type', value || '')}
                    placeholder="Select date type"
                    options={sortedDateTypes}
                    required
                  />
                </div>

                <div>
                  <DatePicker
                    label="Date & Time"
                    size="small"
                    required
                    fullWidth
                    showTime
                    format="DD.MM.YYYY HH:mm"
                    value={date.date_value ? new Date(date.date_value) : null}
                    onChange={(d) => updateImportantDate(index, 'date_value', d ? d.toISOString() : '')}
                  />
                </div>
              </div>

              <div className="mt-3">
                <TextField
                  label="Description"
                  size="small"
                  multiline
                  rows={2}
                  fullWidth
                  value={date.description || ''}
                  onChange={(e) => updateImportantDate(index, 'description', e.target.value)}
                  placeholder="Enter description or notes..."
                />
              </div>

              {date.date_value && (
                <div className="mt-2 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                  Scheduled for: {formatDateTime(date.date_value)}
                </div>
              )}

              {!isValidDate(date) && (
                <div className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Date type and date/time are required
                </div>
              )}
              {DEPARTURE_DATE_TYPES[date.date_type] && !importantDates.some(d => d.date_type === 'start_work') && (
                <div className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  A "{DEPARTURE_DATE_TYPES[date.date_type]}" date requires a "Start Work" date.
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {mode === 'list' && importantDates.length > 0 && (
        <div className="mt-4 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
          {importantDates.filter(d => isValidDate(d)).length} valid date(s) of {importantDates.length} total
        </div>
      )}
    </div>
  );
};

export default ImportantDatesManager;
